import { AUTH_STORAGE_KEYS } from '@/auth/values/auth.values';
import { ScreenMirroringMixin } from '@/screen-mirroring/mixins/screen-mirroring.mixin';
import { LocalStorageService } from '@/services/local-storage.service';
import Component from 'vue-class-component';
import { Mixins } from 'vue-property-decorator';
import { TOOLS_STORE } from '@/tools/store/tools.store';
import { SCHOOL_CALENDAR_STORE } from '@/tools/store/school-calendar.store';
import { APP_EVENT_BUS, APP_STORAGE_KEYS } from '../enums/app.enum';
import { _APP_STORE } from '../values/app.values';
import SocketMixin from './socket.mixin';
import { COURSE_TABS } from '@/courses/values/courses.values';
import { HOMEWORKS_STORE } from '@/homework/store/homeworks.store';
import { AuthProvider } from '@/auth/providers/auth.provider';
import { AUTH_STORE } from '@/auth/store/auth.store';
import { resetAllModuleStates } from '../store/core.store';
import { RequestProvider } from '@/shared/providers/request.provider';
import { Capacitor } from '@capacitor/core';
import { getOS } from '@/components/calls/services/browser/browser-utils';
import { attemptCloseCapacitorApp } from '@/utils/capacitor-utils';
import * as semver from 'semver';

declare const window: any;

@Component({})
export default class SessionMixin extends Mixins(ScreenMirroringMixin, SocketMixin) {

  public idleTime: number = 0;
  public minutesUntilLogout: number = 120;
  public minutesUntilWarning: number = 115;
  public sessionInterval;
  public detectUser: boolean = true;
  public variableTimer: number;
  public intervalTimer = null;

  get institutionId() {
    let institutions = this.$store.state.ToolsStore.institutionInfo;
    return institutions.length > 0 ? institutions[0].organizationId : null;
  }

  get schoolDays() {
    return this.$store.state.SchoolCalendarStore.schooldaysList.dates;
  }

  get versionData() {
    return this.$store.state.AppStore.versionData;
  }

  /**
   * Detects the current platform and returns the appropriate platform string.
   * @returns {string} Platform identifier ("ios", "mac", "windows", "web")
   */
  detectPlatform(): string {
    // Check if we're running in Capacitor (mobile)
    if (Capacitor.isNativePlatform()) {
      const platform = Capacitor.getPlatform();
      if (platform === 'ios') {
        return 'ios';
      }
      return 'ios';
    }

    // Check if we're running in Electron
    if (window.electronAPI) {
      const os = getOS();
      if (os === 'macos') {
        return 'mac';
      } else if (os === 'windows') {
        return 'windows';
      }
      // Default to mac for other Electron platforms
      return 'mac';
    }

    // Default to web for browser environments
    return 'web';
  }

  /**
   * Opens the appropriate store URL based on the current platform
   */
  openStoreUrl() {
    const platform = this.detectPlatform();
    const storeUrl = this.versionData?.storeUrl;

    if (!storeUrl) {
      return;
    }

    if (platform === 'ios' && Capacitor.isNativePlatform()) {
      // For Capacitor iOS, try to use the native URL opening
      // iOS will automatically detect App Store URLs and open them in the App Store
      window.open(storeUrl, '_system');
      // Close the app after opening the store URL
      setTimeout(() => {
        attemptCloseCapacitorApp();
      }, 2000); // Give time for the store to open
    } else if ((platform === 'mac' || platform === 'windows') && window.electronAPI) {
      // For Electron, use the shell to open external URLs
      const newWindow = window.open(storeUrl, '_blank');
      // Close the blank window immediately
      if (newWindow) {
        setTimeout(() => newWindow.close(), 100);
      }
    } else {
      // For web or fallback
      window.open(storeUrl, '_blank');
    }
  }

  /**
   * Checks for app updates based on the current platform
   */
  async checkForUpdates() {
    const platform = this.detectPlatform();
    if (platform !== 'web') {
      const currentVersion = process.env.PACKAGE_VERSION;
      if (!this.versionData?.versionCode) {
        await this.$App.Store.getVersionData(platform);
      }
      if (this.versionData && currentVersion) {
        // Clean versions to ensure they're valid semver
        const cleanCurrentVersion = semver.clean(currentVersion);
        const cleanLatestVersion = semver.clean(this.versionData.versionCode);
        const cleanMinSupportedVersion = semver.clean(this.versionData.minSupportedVersion);


        if (!cleanCurrentVersion || !cleanLatestVersion || !cleanMinSupportedVersion) {
          return;
        }

        // If current version equals latest version, do nothing
        if (semver.eq(cleanCurrentVersion, cleanLatestVersion)) {
          return;
        }

        // If current version is less than minimum supported version, force update
        if (semver.lt(cleanCurrentVersion, cleanMinSupportedVersion)) {
          this.$EdModal.createConfirmationModal({
            title: this.$i18n.t('updateApp.forceUpdateTitle', {version: this.versionData.versionCode}),
            body: this.$i18n.t('updateApp.forceUpdateBody', {version: currentVersion}),
            // actionText: this.$i18n.t('updateApp.updateButton'),
            actions: [
              {
                label: this.$i18n.t('updateApp.updateButton'),
                action: () => {
                  this.openStoreUrl();
                  // Close the main window for mandatory updates
                  window.close();
                }
              }
            ],
          });
        }
        // If current version is less than latest but greater than or equal to minimum supported
        else if (semver.lt(cleanCurrentVersion, cleanLatestVersion) && semver.gte(cleanCurrentVersion, cleanMinSupportedVersion)) {
          this.$EdModal.createConfirmationModal({
            title: this.$i18n.t('updateApp.optionalUpdateTitle', {version: this.versionData.versionCode}),
            body: this.$i18n.t('updateApp.optionalUpdateBody', {version: this.versionData.versionCode}),
            actionText: this.$i18n.t('updateApp.updateButton'),
            action: () => {
              // Handle update action based on platform
              this.openStoreUrl();
              window.close();
            },
            secondActionText: this.$i18n.t('updateApp.cancelButton'),
            secondAction: () => {
              // User chose to update later, do nothing
            }
          });
        }
      }
    }
  }

  async setAppReady() {
    this.$App.Store.setAppReady(true);
    this.$edEventBus.$emit(APP_EVENT_BUS.APP_READY);
    this.$SplashScreen.hide();
  }

  async initSchoolCalendar() {
    if(this.institutionId) {
      await this.$store.dispatch(
        SCHOOL_CALENDAR_STORE.ACTIONS.GET_SCHOOLDAYS_LIST, 
        { organizationId: this.institutionId }
      );
      if(this.schoolDays && this.schoolDays.length > 0) {
        await this.$store.dispatch(
          SCHOOL_CALENDAR_STORE.ACTIONS.GET_HOLIDAYS, 
          {
            organizationId: this.institutionId,
            startDate: this.schoolDays[0],
            year: '',
            endDate: this.schoolDays[this.schoolDays.length - 1]
          }
        );
        if(!this.$Auth.Store.isStudent) {
          await this.$store.dispatch(
            SCHOOL_CALENDAR_STORE.ACTIONS.GET_COLORS_PREFERENCE,
            this.institutionId
          );
        }
      }
    }
    this.$App.Store.setActiveSchoolDaysLoaded(true);
  }

  async initInstitution() {
    await this.$store.dispatch(TOOLS_STORE.ACTIONS.GET_INSTITUTION_INFO);
    this.$App.Store.setInstitutionsLoaded(true);
    this.$edEventBus.$emit(APP_EVENT_BUS.APP_INSTITUTION_READY);
    this.initSchoolCalendar();
  }

  async initSession() {
    try {
      if (AuthProvider.getIsCurrentAccessTokenActive()) {
        this.$SplashScreen.show();
        this.$Auth.Store.setSession();
        await this.$Auth.Store.getUserLogged();
        return;
      } else {
        return;
      }
    } catch(error) {
      this.logout();
      return;
    }
  }

  async createSession() {
    await this.initInstitution();
    await this.createSocketSession();
    this.initScreenMirroring();
    this.startToVerifyActivity();
  }

  async startSession() {
    const data = await this.$App.Store.getInitialData();
    this.$store.commit(HOMEWORKS_STORE.MUTATIONS.SET_BOARD_PARAMETERS, {
      wboBlockedTools: data.wboBlockedTools,
      wboMaxBoardSize: data.wboMaxBoardSize,
      wboMaxEmitCountPeriod: data.wboMaxEmitCountPeriod,
      wboMaxEmitCount: data.wboMaxEmitCount,
      wboMaxPencilPoints: data.wboMaxPencilPoints
    });
    this.$store.commit(AUTH_STORE.MUTATIONS.SET_SERVERS_URL, data);
    await this.initSession();
    if(this.$Auth.Store.hasSession) {
      await this.createSession();
    }
    await this.checkForUpdates();
    this.setAppReady();
  }

  startToVerifyActivity() {
    this.sessionInterval = window.setInterval(this.inactivityIncrement, 60000);
    window.addEventListener('keypress',() => this.resetIdle());
    window.addEventListener('mousedown',() => this.resetIdle());
    window.addEventListener('mousemove',() => this.resetIdle());
  }

  resetIdle(){
    if(this.detectUser) {
      this.idleTime = 0;
    }
  }

  inactivityIncrement() {
    this.idleTime++;
    if(this.idleTime >= this.minutesUntilWarning) {
      if(this.idleTime >= this.minutesUntilLogout) {
        this.clearVariables();
        this.logout();
      }
      else if(this.idleTime == this.minutesUntilWarning){
        this.$EdModal.createConfirmationModal(
          {
            title: this.$i18n.t( 'inactivityTimeout.modalTitle' ),
            body: this.modalHtmlBody(this.minutesUntilLogout - this.minutesUntilWarning),
            actionText: this.$i18n.t( 'inactivityTimeout.modalOption1' ),
            action: () => this.clearVariables(),
            secondActionText:this.$i18n.t( 'inactivityTimeout.modalOption2' ),
            secondAction: () => {
              this.clearVariables();
              this.logout();
              this.clearLocalStorage();
            },
            htmlBody:true
          }
        );
        this.detectUser = false;

        this.variableTimer = (this.minutesUntilLogout - this.minutesUntilWarning) * 60;
        this.intervalTimer = window.setInterval(this.refreshModalBody, 1000);
      }
    }
  }

  refreshModalBody() {
    this.variableTimer--;
    var minutes = Math.trunc(this.variableTimer / 60);
    var seconds = Math.trunc(this.variableTimer % 60);
    this.$EdModal.refreshBody(this.modalHtmlBody(minutes,seconds));
  }

  modalHtmlBody(minutes:number, seconds:number = 0):string {
    var strMinutes = minutes < 10 ? '0' + minutes : minutes+'';
    var strSeconds = seconds < 10 ? '0' + seconds : seconds+'';
    var body = `<p>${this.$i18n.t( 'inactivityTimeout.modalBody1' )}</p>
                <p style="color:var(--ed-color-green);width:100%;text-align:center">${strMinutes}:${strSeconds}</p>
                <p>${this.$i18n.t( 'inactivityTimeout.modalBody2' )}</p>`
    return body;
  }

  clearVariables(){
    this.idleTime = 0;
    this.detectUser = true;
    window.clearInterval(this.intervalTimer);
  }

  clearLocalStorage() {
    LocalStorageService.setItem(COURSE_TABS.ED_PORTAL_TAB, COURSE_TABS.MAIN_TAB);
    LocalStorageService.removeItem(COURSE_TABS.COURSE_OBJ);
    LocalStorageService.removeItem(COURSE_TABS.COURSE_ID);
  }

  logout(redirect = true) {
    window.removeEventListener('keypress',() => this.resetIdle());
    window.removeEventListener('mousedown',() => this.resetIdle());
    window.removeEventListener('mousemove',() => this.resetIdle());
    // End Media access while the browser can still send its cookie. A failed cleanup must not block logout.
    RequestProvider.revokeMediaSession().catch(() => undefined);
    LocalStorageService.removeItem(AUTH_STORAGE_KEYS.ED_JWT);
    LocalStorageService.removeItem(AUTH_STORAGE_KEYS.ED_REFRESH_TOKEN);
    LocalStorageService.setItem(COURSE_TABS.ED_PORTAL_TAB, COURSE_TABS.MAIN_TAB);
    LocalStorageService.removeItem(COURSE_TABS.COURSE_OBJ);
    LocalStorageService.removeItem(COURSE_TABS.COURSE_ID);
    LocalStorageService.removeItem(APP_STORAGE_KEYS.STUDENT_MODE);
    this.$Auth.Store.cleanSession();

    if(redirect) {
      this.$EdSocket.disconnectManually();
      resetAllModuleStates();
      LocalStorageService.clearAllStorage();
      setTimeout(() =>  window.location.href = '/', 100);  // Force the release of calls resources
    }
  }
  
}
